import {
    faCheck,
    faCircleCheck,
    faClock,
    faEllipsisVertical,
    faNote,
    faTrash,
    type IconDefinition,
} from "@fortawesome/pro-light-svg-icons";
import { faEdit } from "@fortawesome/pro-light-svg-icons/faEdit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TimelineDot, type TimelineDotProps } from "@mui/lab";
import type { Theme } from "@mui/system";
import { Tag } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Toggle,
    Tooltip,
    useTheme,
} from "@vapor/react-material";
import type { TFunction } from "i18next";
import { capitalize } from "lodash";
import { type ReactElement, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteActivity } from "../../../../../core/api/use-delete-activity/useDeleteActivity";
import {
    ActivitySource,
    type FulfilmentActivityDto,
} from "../../../../../core/api/use-get-fulfilment-activities/FulfilmentActivityDto";
import { FulfilmentActivityType } from "../../../../../core/api/use-get-fulfilment-activities/FulfilmentActivityType";
import { FulfilmentStatus } from "../../../../../core/api/use-get-fulfilment-detail/FulfilmentStatus";
import { useUpdateActivity } from "../../../../../core/api/use-update-activity/useUpdateActivity";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";
import { usePermission } from "../../../../../core/permissions/usePermission";
import { Format, formatDate } from "../../../../../utils/dateUtils";

interface FulfilmentActivityProps {
    activity: FulfilmentActivityDto;
    fulfilmentStatus: FulfilmentStatus;
    levyId: string;
    onClickActivityEditTitle: (activity: FulfilmentActivityDto) => void;
    onClickActivityViewNote: (activity: FulfilmentActivityDto) => void;
    onUpdateActivity: () => void;
    taxPayerWorkspaceId: string;
    width?: string | number;
    year: number;
}

interface FulfilmentActivityOption {
    divider?: boolean;
    icon: IconDefinition;
    label: string;
    onClick?: () => void;
    spinner?: JSX.Element;
    toggle?: JSX.Element;
}

const getTimelineDot = (
    event: FulfilmentActivityType,
    theme: Theme,
    isActivityDone?: boolean,
) => {
    let icon;
    let color: TimelineDotProps["color"];
    switch (event) {
        case FulfilmentActivityType.Create:
            if (isActivityDone === true) {
                icon = <FontAwesomeIcon icon={faCheck} size="sm" />;
                color = "primary";
            } else {
                icon = (
                    <FontAwesomeIcon
                        color={theme.palette.primary.gainsboro}
                        icon={faClock}
                        size="sm"
                    />
                );
                color = "grey";
            }
            break;
        default:
            icon = <FontAwesomeIcon icon={faCheck} size="sm" />;
            color = "primary";
    }

    return (
        <TimelineDot
            color={color}
            variant="outlined"
            sx={{
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                my: 0,
            }}
        >
            {icon}
        </TimelineDot>
    );
};

const translateFulfilmentActivityType = (
    event: FulfilmentActivityType,
    isActivityDone: boolean,
    t: TFunction,
) => {
    if (event === FulfilmentActivityType.Create && !isActivityDone) {
        return t("entity.fulfilment.activity.status.to_be_completed.singular");
    } else {
        return t("entity.fulfilment.activity.status.completed.singular");
    }
};

export const FulfilmentActivity = ({
    activity,
    fulfilmentStatus,
    levyId,
    onClickActivityEditTitle,
    onClickActivityViewNote,
    onUpdateActivity,
    taxPayerWorkspaceId,
    year,
}: FulfilmentActivityProps): ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();
    const canManageActivities = usePermission("activityManage");

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
        useState<boolean>(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);

    const deleteActivityMutation = useDeleteActivity();
    const updateActivityMutation = useUpdateActivity();

    const onClickCancelDialog = () => {
        setIsDeleteDialogOpen(false);
    };

    const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(null);
    };

    const handleOnClickActivityCompleted = useCallback(async () => {
        try {
            await updateActivityMutation.mutateAsync({
                activityId: activity.id,
                isDone: !activity.isDone,
                taxPayerWorkspaceId: taxPayerWorkspaceId,
                levyId: levyId,
                year: year,
            });
        } catch {
            return;
        }
        if (!activity.isDone) {
            showTextMessage(
                t(
                    "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.menu_item.activity_completed.success",
                ),
            );
        } else {
            showTextMessage(
                t(
                    "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.menu_item.activity_to_be_completed.success",
                ),
            );
        }
        onUpdateActivity();
    }, [
        updateActivityMutation,
        activity.id,
        activity.isDone,
        taxPayerWorkspaceId,
        levyId,
        year,
        onUpdateActivity,
        showTextMessage,
        t,
    ]);

    const handleOnClickActivityEditTitle = useCallback(() => {
        setMenuAnchorEl(null);
        onClickActivityEditTitle(activity);
    }, [activity, onClickActivityEditTitle]);

    const handleOnClickActivityViewNote = useCallback(() => {
        setMenuAnchorEl(null);
        onClickActivityViewNote(activity);
    }, [activity, onClickActivityViewNote]);

    const handleOnClickDeleteActivity = async () => {
        try {
            await deleteActivityMutation.mutateAsync({
                activityId: activity.id,
                taxPayerWorkspaceId: taxPayerWorkspaceId,
                levyId: levyId,
                year: year,
            });
        } catch {
            return;
        }
        showTextMessage(
            t(
                "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.delete.successfull",
            ),
        );
        onUpdateActivity();
    };

    const onClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const onClickShowDeleteNoteDialog = () => {
        setMenuAnchorEl(null);
        setIsDeleteDialogOpen(true);
    };

    const getActivityCompletedButton = useCallback(() => {
        const activityCompletedButton = {
            icon: faCircleCheck,
            label: t(
                "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.menu_item.activity_completed",
            ),
            spinner: <CircularProgress color="inherit" size={16} />,
            toggle: (
                <Toggle
                    checked={activity.isDone ? true : false}
                    onClick={handleOnClickActivityCompleted}
                    disabled={updateActivityMutation.isPending}
                ></Toggle>
            ),
        };
        return activityCompletedButton;
    }, [
        activity.isDone,
        handleOnClickActivityCompleted,
        updateActivityMutation.isPending,
        t,
    ]);

    const getDeleteActivityButton = useCallback(() => {
        const deleteActivityButton = {
            divider: true,
            icon: faTrash,
            label: t(
                "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.menu_item.delete_activity",
            ),
            onClick: () => onClickShowDeleteNoteDialog(),
        };
        return deleteActivityButton;
    }, [t]);

    const getEditActivityTitle = useCallback(() => {
        const editActivityTitle = {
            icon: faEdit,
            label: t(
                "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.menu_item.edit_activity_title",
            ),
            onClick: () => handleOnClickActivityEditTitle(),
        };
        return editActivityTitle;
    }, [handleOnClickActivityEditTitle, t]);

    const getViewNotesButton = useCallback(() => {
        const viewNoteButton = {
            icon: faNote,
            label: t(
                "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.menu_item.view_note",
            ),
            onClick: () => handleOnClickActivityViewNote(),
        };
        return viewNoteButton;
    }, [handleOnClickActivityViewNote, t]);

    const isActivityCreatedFromFrontEnd = useCallback(() => {
        return (
            activity.type === FulfilmentActivityType.Create &&
            activity.source === ActivitySource.FRONTEND
        );
    }, [activity.type, activity.source]);

    const options = useMemo(() => {
        const options: FulfilmentActivityOption[] = [];
        if (activity && isActivityCreatedFromFrontEnd()) {
            if (
                canManageActivities &&
                fulfilmentStatus !== FulfilmentStatus.COMPLETED
            ) {
                options.push(getEditActivityTitle());
            }
            if (activity.notes) {
                options.push(getViewNotesButton());
            }
            if (
                canManageActivities &&
                fulfilmentStatus !== FulfilmentStatus.COMPLETED
            ) {
                options.push(getActivityCompletedButton());
            }
            if (
                canManageActivities &&
                (fulfilmentStatus === FulfilmentStatus.PENDING ||
                    fulfilmentStatus === FulfilmentStatus.CLOSED)
            ) {
                options.push(getDeleteActivityButton());
            }
        }
        return options;
    }, [
        activity,
        fulfilmentStatus,
        isActivityCreatedFromFrontEnd,
        canManageActivities,
        getEditActivityTitle,
        getViewNotesButton,
        getActivityCompletedButton,
        getDeleteActivityButton,
    ]);

    return (
        <Stack
            alignItems="center"
            border="1px solid"
            flexDirection="row"
            justifyContent="space-between"
            gap={4}
            p={3}
            sx={{
                border: `1px solid ${theme.palette.primary.columbiaBlue}`,
                borderRadius: "4px",
            }}
        >
            <Stack
                flexDirection="row"
                gap={2.5}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    display: "flex",
                    maxWidth: "100%",
                }}
            >
                <Box>
                    <Tooltip
                        title={translateFulfilmentActivityType(
                            activity.type,
                            activity.isDone ?? false,
                            t,
                        )}
                        placement="top"
                    >
                        {getTimelineDot(activity.type, theme, activity.isDone)}
                    </Tooltip>
                </Box>
                <Stack
                    gap={0.5}
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    <Typography
                        sx={{
                            cursor: "pointer",
                            ":hover": {
                                color: "rgb(0, 144, 209)",
                            },
                        }}
                        color={theme.palette.text.primary}
                        variant="titleSmall"
                        display="block"
                        overflow="hidden"
                        textOverflow="ellipsis"
                    >
                        {capitalize(activity.title)}
                    </Typography>
                    {activity.operator && (
                        <Typography
                            variant="body"
                            color={theme.palette.primary.textSubduedColor}
                        >
                            {`${activity.operator.givenName} ${activity.operator.familyName}`}
                        </Typography>
                    )}
                    <Typography
                        variant="bodySmall"
                        color={theme.palette.primary.textTitleColor}
                    >
                        {formatDate(
                            activity.timestamp,
                            Format.FULL_DATE_AND_TIME,
                        )}
                    </Typography>
                </Stack>
            </Stack>
            {activity.operator && (
                <Stack
                    direction="column"
                    justifyContent={"space-between"}
                    spacing={1}
                >
                    <Stack
                        direction="row"
                        justifyContent={"center"}
                        spacing={1}
                    >
                        {activity.notes && (
                            <Tooltip
                                title={t(
                                    "views.fulfilment_detail.components.add_activity.note.label",
                                )}
                                placement="top"
                            >
                                <Box
                                    sx={{
                                        cursor: "pointer",
                                    }}
                                    margin={1}
                                >
                                    <FontAwesomeIcon
                                        icon={faNote}
                                        color={
                                            theme.palette.primary.textTitleColor
                                        }
                                        cursor="auto"
                                        size="lg"
                                    />
                                </Box>
                            </Tooltip>
                        )}
                        <Tag
                            label={t(
                                "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.type.manual",
                            )}
                            variant="standard"
                            type="blueSapphire"
                        />
                    </Stack>
                    {options.length > 0 && (
                        <Stack direction="row" justifyContent="flex-end">
                            <IconButton
                                aria-controls="menu"
                                aria-haspopup="true"
                                onClick={onClickMenu}
                                size="small"
                            >
                                <FontAwesomeIcon
                                    color={
                                        theme.palette.primary.interactiveDefault
                                    }
                                    icon={faEllipsisVertical}
                                    size="xl"
                                    fontWeight={400}
                                />
                            </IconButton>
                            <Menu
                                id="menu"
                                anchorEl={menuAnchorEl}
                                open={options.length > 0 && !!menuAnchorEl}
                                onClose={handleCloseMenu}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                }}
                            >
                                {options.map((option, index) => (
                                    <Box key={index}>
                                        {options.length > 1 &&
                                            option &&
                                            option.divider && (
                                                <Divider className="MuiDivider-VaporLight" />
                                            )}
                                        <MenuItem
                                            key={index}
                                            onClick={option?.onClick}
                                        >
                                            <>
                                                {option.spinner &&
                                                updateActivityMutation.isPending ? (
                                                    option.spinner
                                                ) : (
                                                    <VaporIcon
                                                        icon={option?.icon}
                                                        color={
                                                            theme.palette
                                                                .primary
                                                                .textTitleColor
                                                        }
                                                    />
                                                )}
                                            </>
                                            <Box sx={{ mr: 1 }}></Box>
                                            <Typography
                                                color={
                                                    theme.palette.primary
                                                        .textTitleColor
                                                }
                                            >
                                                {option?.label}
                                            </Typography>
                                            {option.toggle}
                                        </MenuItem>
                                    </Box>
                                ))}
                            </Menu>
                        </Stack>
                    )}
                </Stack>
            )}
            <Dialog open={isDeleteDialogOpen} onClose={onClickCancelDialog}>
                <DialogTitle>
                    {t(
                        "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.title",
                    )}
                </DialogTitle>
                <Divider variant="fullWidth" />
                <DialogContent>
                    <DialogContentText>
                        {t(
                            "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.content",
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={deleteActivityMutation.isPending}
                        onClick={onClickCancelDialog}
                        variant="outlined"
                    >
                        {t(
                            "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.dialog.actions.cancel",
                        )}
                    </Button>
                    <Button
                        disabled={deleteActivityMutation.isPending}
                        color="error"
                        onClick={handleOnClickDeleteActivity}
                        variant="contained"
                    >
                        {t(
                            "views.fulfilment_detail.components.fulfilment_activity_timeline.activity.menu_item.delete_activity",
                        )}
                        {deleteActivityMutation.isPending && (
                            <CircularProgress
                                color="inherit"
                                sx={{ ml: 1 }}
                                size={16}
                            />
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
